import React, { useState, createElement } from 'react';
import { Copy, Check, Download } from 'lucide-react';
// The favicon SVG markup, shared so it stays in sync between the preview and the copy/download.
const FaviconSVG = ({
  size = 512
}: {
  size?: number;
}) => <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width={size} height={size} role="img" aria-label="Dispel Your Tattoo — favicon" data-id="element-862">
    {/* Background — rounded square in brand-darker */}
    <rect width="512" height="512" rx="96" ry="96" fill="#0a0a0a" data-id="element-863" />

    {/* Geometric bold D in brand-light, with cut-out counter (evenodd) */}
    <path fillRule="evenodd" clipRule="evenodd" fill="#f5f5f0" d="
        M 120 96
        L 256 96
        C 360 96 432 168 432 256
        C 432 344 360 416 256 416
        L 120 416
        Z
        M 192 168
        L 256 168
        C 320 168 360 204 360 256
        C 360 308 320 344 256 344
        L 192 344
        Z
      " data-id="element-864" />

    {/* Laser dot — soft outer halo */}
    <circle cx="404" cy="120" r="40" fill="#f5f5f0" opacity="0.12" data-id="element-865" />
    <circle cx="404" cy="120" r="26" fill="#f5f5f0" opacity="0.25" data-id="element-866" />
    {/* Laser dot — bright core */}
    <circle cx="404" cy="120" r="14" fill="#f5f5f0" data-id="element-867" />
  </svg>;
const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="512" height="512">
  <rect width="512" height="512" rx="96" ry="96" fill="#0a0a0a"/>
  <path fill-rule="evenodd" clip-rule="evenodd" fill="#f5f5f0"
        d="M120 96 L256 96 C360 96 432 168 432 256 C432 344 360 416 256 416 L120 416 Z
           M192 168 L256 168 C320 168 360 204 360 256 C360 308 320 344 256 344 L192 344 Z"/>
  <circle cx="404" cy="120" r="40" fill="#f5f5f0" opacity="0.12"/>
  <circle cx="404" cy="120" r="26" fill="#f5f5f0" opacity="0.25"/>
  <circle cx="404" cy="120" r="14" fill="#f5f5f0"/>
</svg>`;
export const Favicon = () => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(svgMarkup);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleDownloadSVG = () => {
    const blob = new Blob([svgMarkup], {
      type: 'image/svg+xml'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dispel-favicon.svg';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  return <main className="pt-32 pb-24 bg-brand-dark min-h-screen" data-id="element-868">
      <div className="max-w-[900px] mx-auto px-6 md:px-12" data-id="element-869">
        <h1 className="text-4xl md:text-5xl font-medium text-brand-light mb-4 tracking-tight" data-id="element-870">
          Favicon Preview
        </h1>
        <p className="text-brand-gray font-light leading-relaxed mb-12 max-w-2xl" data-id="element-871">
          A geometric "D" monogram with a laser dot. Right-click any size below
          to save as PNG, or use the buttons to download the source SVG. For
          best results, convert the 512px version to ICO using
          realfavicongenerator.net.
        </p>

        {/* Hero preview */}
        <div className="bg-brand-darker border border-brand-border/30 rounded-3xl p-12 mb-12 flex justify-center" data-id="element-872">
          <div className="w-[320px] h-[320px]" data-id="element-873">
            <FaviconSVG size={320} data-id="element-874" />
          </div>
        </div>

        {/* Size grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-12" data-id="element-875">
          {[16, 32, 64, 128].map(size => <div key={size} className="bg-brand-darker border border-brand-border/30 rounded-2xl p-6 flex flex-col items-center" data-id="element-876">
              <div className="flex items-center justify-center mb-4" style={{
            height: 128
          }} data-id="element-877">
                <FaviconSVG size={size} data-id="element-878" />
              </div>
              <span className="text-xs font-medium text-brand-gray uppercase tracking-widest" data-id="element-879">
                {size} × {size}
              </span>
            </div>)}
        </div>

        {/* Tab strip mockup — shows how it looks in a browser tab */}
        <div className="mb-12" data-id="element-880">
          <p className="text-sm font-medium text-brand-gray uppercase tracking-widest mb-4" data-id="element-881">
            Browser tab preview
          </p>
          <div className="bg-brand-darker border border-brand-border/30 rounded-t-xl p-3 flex items-center space-x-3 max-w-md" data-id="element-882">
            <div className="w-4 h-4 shrink-0" data-id="element-883">
              <FaviconSVG size={16} data-id="element-884" />
            </div>
            <span className="text-sm text-brand-light truncate" data-id="element-885">
              Dispel Your Tattoo — Détatouage Martigny
            </span>
            <span className="text-brand-gray text-lg leading-none ml-auto" data-id="element-886">
              ×
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-12" data-id="element-887">
          <button onClick={handleDownloadSVG} className="inline-flex items-center space-x-2 px-6 py-3 bg-brand-light text-brand-dark rounded-full font-bold uppercase tracking-wider hover:bg-white transition-colors text-sm" data-id="element-888">
            <Download size={16} data-id="element-889" />
            <span data-id="element-890">Télécharger le SVG</span>
          </button>
          <button onClick={handleCopy} className="inline-flex items-center space-x-2 px-6 py-3 border border-brand-border text-brand-light rounded-full font-bold uppercase tracking-wider hover:border-brand-light transition-colors text-sm" data-id="element-891">
            {copied ? <Check size={16} data-id="element-892" /> : <Copy size={16} data-id="element-893" />}
            <span data-id="element-894">{copied ? 'Copié' : 'Copier le code SVG'}</span>
          </button>
        </div>

        {/* Raw SVG */}
        <div className="bg-brand-darker border border-brand-border/30 rounded-2xl p-6 overflow-x-auto" data-id="element-895">
          <pre className="text-xs text-brand-gray font-mono leading-relaxed whitespace-pre" data-id="element-896">
            {svgMarkup}
          </pre>
        </div>

        {/* Export instructions */}
        <div className="mt-12 bg-brand-darker border border-brand-border/30 rounded-2xl p-8" data-id="element-897">
          <h2 className="text-xl font-medium text-brand-light mb-4" data-id="element-898">
            Comment exporter en PNG
          </h2>
          <ol className="text-brand-gray font-light leading-relaxed space-y-2 list-decimal list-inside text-sm" data-id="element-899">
            <li data-id="element-900">
              Cliquez sur "Télécharger le SVG" ou faites un clic droit sur la
              grande prévisualisation → "Enregistrer l'image sous…"
            </li>
            <li data-id="element-901">
              Ouvrez le SVG dans Figma, Sketch ou un convertisseur en ligne
              comme svgtopng.com
            </li>
            <li data-id="element-902">
              Exportez aux tailles requises : 16, 32, 48, 180 (Apple touch),
              192, 512 px
            </li>
            <li data-id="element-903">
              Pour générer un fichier .ico multi-résolution, utilisez{' '}
              <a href="https://realfavicongenerator.net" target="_blank" rel="noopener noreferrer" className="text-brand-light underline" data-id="element-904">
                realfavicongenerator.net
              </a>
            </li>
            <li data-id="element-905">
              Téléversez le favicon via le bouton <strong data-id="element-906">PUBLISH</strong> en
              haut à droite de l'éditeur Magic Patterns
            </li>
          </ol>
        </div>
      </div>
    </main>;
};